﻿import { Control, Controller } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { Link } from "@/components/Interface/Link/Link";
import { Notification } from "@/components/Interface/Notification/Notification";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { PaymentProvider } from "@/components/Layout/Payment/PaymentProvider/PaymentProvider";
import {
  paymentProviderIcons,
  paymentProviderLabels,
} from "@/components/Layout/Payment/PaymentProviderPicker/PaymentProviderPicker";
import { SubscriptionInfoBox } from "@/components/Layout/SubscriptionInfoBox/SubscriptionInfoBox";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { DrCreditCardCardType } from "@/services/model";

interface CheckoutPaymentCardProps {
  isInvoice: boolean;
  isCreditCard: boolean;
  cardType?: DrCreditCardCardType;
  maskedCardNumberString: string;
  /** Drives the provider picker shown when no payment mean is saved. */
  control: Control<{ paymentProvider: DrCreditCardCardType }>;
  /** When true, renders the "No charge" UX instead of requiring a payment method selection. */
  sumIsZero?: boolean;
  dataTestid?: string;
}

/**
 * The "Payment method" card of the redesigned checkouts: the saved payment
 * mean when one exists, the provider picker otherwise, and the hint that
 * changing it goes through the account settings.
 */
export const CheckoutPaymentCard = ({
  isInvoice,
  isCreditCard,
  cardType,
  maskedCardNumberString,
  control,
  sumIsZero = false,
  dataTestid = "checkout-payment-card",
}: CheckoutPaymentCardProps) => {
  const { t: tDefault } = useTranslation();

  return (
    <CardModule
      dataTestid={dataTestid}
      variant="white"
      bodyGap={2}
      title={tDefault("portal:common.checkout.paymentMethod")}
      subtitle={
        isInvoice || isCreditCard
          ? tDefault("portal:common.checkout.paymentMethodSubtitle")
          : undefined
      }
    >
      {isInvoice || isCreditCard ? (
        <SubscriptionInfoBox className="flex items-center gap-3">
          {isCreditCard && cardType != null && (
            <span className="shrink-0">{paymentProviderIcons[cardType]}</span>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <p className="truncate-safe text-body-sm font-semibold leading-none text-foreground-primary">
              {isInvoice
                ? tDefault("portal:user-account.paymentMethods.invoice")
                : cardType != null && paymentProviderLabels[cardType]}
            </p>
            {isCreditCard && (
              <p className="truncate-safe text-body-sm leading-none text-foreground-primary">
                {maskedCardNumberString}
              </p>
            )}
          </div>
        </SubscriptionInfoBox>
      ) : (
        <Controller
          control={control}
          name="paymentProvider"
          render={({ field: { onChange, value } }) => (
            <PaymentProvider
              paymentProvider={value}
              sumIsZero={sumIsZero}
              onChange={onChange}
            />
          )}
        />
      )}

      {!isInvoice && (
        <Notification
          dataTestid={`${dataTestid}-hint`}
          description={
            <Trans
              t={tDefault}
              i18nKey="portal:common.checkout.paymentMethodHint"
              components={[
                <Link
                  key="settings-link"
                  size="sm"
                  to={portalRoutes.documents.payments}
                />,
              ]}
            />
          }
        />
      )}
    </CardModule>
  );
};
